// Merchant list + per-row subscription editor (tier, status, commission_rate,
// monthly_fee, current_period_end). Subscriptions are admin-managed in this
// build — no payments processor wired in.

import React, { useEffect, useState } from 'react';
import { listMerchants, upsertSubscription } from '../../services/adminService';
import DataTable from '../common/DataTable';
import PortalModal, { ModalActions } from '../common/PortalModal';
import Field from '../common/Field';

const TIERS = ['free', 'basic', 'pro'];
const STATUSES = ['active', 'past_due', 'canceled'];

export default function AdminMerchants() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editing, setEditing] = useState(null);   // store object
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reload = () => listMerchants().then(d => setRows(d.merchants || [])).catch(() => {});
  useEffect(() => { reload(); }, []);

  const needle = q.trim().toLowerCase();
  const visible = rows.filter(m => {
    if (needle) {
      const hay = `${m.name} ${m.owner?.email || ''} ${m.owner?.username || ''}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    if (tierFilter) {
      if (tierFilter === 'none' && m.subscription) return false;
      if (tierFilter !== 'none' && m.subscription?.tier !== tierFilter) return false;
    }
    if (statusFilter && m.subscription?.status !== statusFilter) return false;
    return true;
  });

  const resetFilters = () => { setQ(''); setTierFilter(''); setStatusFilter(''); };
  const hasFilters = q || tierFilter || statusFilter;

  const openEdit = (m) => {
    const sub = m.subscription || {};
    setEditing(m);
    setForm({
      tier: sub.tier || 'free',
      status: sub.status || 'active',
      commission_rate: sub.commission_rate || '0',
      monthly_fee: sub.monthly_fee || '0',
      current_period_end: sub.current_period_end ? sub.current_period_end.slice(0, 10) : '',
      notes: sub.notes || '',
    });
    setError('');
  };

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    setError('');
    try {
      const body = { ...form };
      if (body.current_period_end) body.current_period_end = `${body.current_period_end}T00:00:00Z`;
      else delete body.current_period_end;
      await upsertSubscription(editing.id, body);
      setEditing(null);
      reload();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="portal-content-header">
        <h1>Merchants</h1>
      </div>

      <div className="portal-filter-bar">
        <input
          type="search"
          className="portal-search"
          placeholder="Search by store or owner"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        <select className="portal-search" value={tierFilter} onChange={e => setTierFilter(e.target.value)}>
          <option value="">Any tier</option>
          <option value="none">No subscription</option>
          {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="portal-search" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">Any status</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {hasFilters && <button type="button" className="portal-button secondary" onClick={resetFilters}>Clear</button>}
        <span className="meta">{visible.length} of {rows.length} merchants</span>
      </div>

      <DataTable
        columns={[
          { key: 'name', label: 'Store', render: m => <strong>{m.name}</strong> },
          { key: 'owner', label: 'Owner', render: m => m.owner ? (m.owner.email || m.owner.username) : '—' },
          { key: 'tier', label: 'Tier', render: m => m.subscription
              ? <span className={`badge badge-${m.subscription.status === 'active' ? 'active' : 'pending'}`}>{m.subscription.tier}</span>
              : <em style={{ color: 'var(--tudu-text-lo)' }}>none</em> },
          { key: 'commission', label: 'Commission', render: m => m.subscription ? `${(parseFloat(m.subscription.commission_rate) * 100).toFixed(2)}%` : '—' },
          { key: 'fee', label: 'Monthly fee', render: m => m.subscription ? `$${m.subscription.monthly_fee}` : '—' },
          { key: 'flyers_count', label: 'Flyers' },
          { key: 'coupons_count', label: 'Coupons' },
          { key: 'actions', label: '', render: m => (
            <button type="button" className="portal-button" onClick={() => openEdit(m)}>
              {m.subscription ? 'Edit subscription' : 'Create subscription'}
            </button>
          ) },
        ]}
        rows={visible}
        emptyMessage={hasFilters ? 'No merchants match the current filters.' : 'No merchant accounts yet. Assign an owner to a store first.'}
      />

      <PortalModal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing ? `Subscription · ${editing.name}` : ''}
        size="narrow"
      >
        {error && <div className="portal-error">{error}</div>}
        <div className="portal-form-grid">
          <Field label="Tier">
            <select className="portal-search" value={form.tier || ''} onChange={e => setForm({ ...form, tier: e.target.value })}>
              {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select className="portal-search" value={form.status || ''} onChange={e => setForm({ ...form, status: e.target.value })}>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Commission rate (0–1, e.g. 0.05 = 5%)">
            <input className="portal-search" value={form.commission_rate || ''} onChange={e => setForm({ ...form, commission_rate: e.target.value })} />
          </Field>
          <Field label="Monthly fee ($)">
            <input className="portal-search" value={form.monthly_fee || ''} onChange={e => setForm({ ...form, monthly_fee: e.target.value })} />
          </Field>
          <Field label="Current period end">
            <input type="date" className="portal-search" value={form.current_period_end || ''} onChange={e => setForm({ ...form, current_period_end: e.target.value })} />
          </Field>
          <Field label="Notes" full>
            <textarea className="portal-textarea" value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </Field>
        </div>
        <ModalActions>
          <button type="button" className="portal-button secondary" onClick={() => setEditing(null)}>Cancel</button>
          <button type="button" className="portal-button" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        </ModalActions>
      </PortalModal>
    </>
  );
}
